import { lazy } from 'react'
import { registerPreview, registerEditor } from '../editor-registry.js'

// ─── Lazy-loaded preview components ──────────────────────────────────

const LazyImageViewer = lazy(() =>
  import('../../ui/ImageViewer.js').then(m => ({ default: m.ImageViewer }))
)

const LazyVideoViewer = lazy(() =>
  import('../../ui/VideoViewer.js').then(m => ({ default: m.VideoViewer }))
)

const LazyAudioViewer = lazy(() =>
  import('../../ui/AudioViewer.js').then(m => ({ default: m.AudioViewer }))
)

// Media EDITOR wrapper (toolbar + Share) for image/video/audio at /file/:id.
const LazyMediaEditor = lazy(() =>
  import('../../ui/MediaEditor.js').then(m => ({ default: m.MediaEditor }))
)

const LazyPdfViewer = lazy(() =>
  import('../../ui/PdfViewer.js').then(m => ({ default: m.PdfViewer }))
)

const LazyTextViewer = lazy(() =>
  import('../../ui/TextViewer.js').then(m => ({ default: m.TextViewer }))
)

const LazyCsvViewer = lazy(() =>
  import('../../ui/CsvViewer.js').then(m => ({ default: m.CsvViewer }))
)

// ─── Register previews ───────────────────────────────────────────────

registerPreview({
  extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'],
  name: 'Image Viewer',
  component: LazyImageViewer,
})

registerPreview({
  extensions: ['mp4', 'webm', 'ogg', 'mov'],
  name: 'Video Player',
  component: LazyVideoViewer,
})

registerPreview({
  extensions: ['mp3', 'wav', 'm4a', 'aac', 'flac', 'aiff', 'opus', 'oga', 'weba'],
  name: 'Audio Player',
  component: LazyAudioViewer,
})

registerPreview({
  extensions: ['pdf'],
  name: 'PDF Viewer',
  component: LazyPdfViewer,
})

registerPreview({
  extensions: ['md', 'txt', 'json', 'pmeta', 'log', 'html', 'htm', 'yaml', 'yml'],
  name: 'Text Preview',
  component: LazyTextViewer,
})

registerPreview({
  extensions: ['csv', 'tsv'],
  name: 'CSV Preview',
  component: LazyCsvViewer,
})

// ─── Register read-only editors for images, video, audio, pdf ────────
// These allow these file types to work at /file/:fileId too (TabContent).
// Image/video/audio/pdf share ONE MediaEditor wrapper so they get the toolbar
// + Share control (the bare viewers above are content-only, used by the
// preview pane).

registerEditor({
  family: 'media',
  extensions: [
    // image
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico',
    // video
    'mp4', 'webm', 'ogg', 'mov',
    // audio
    'mp3', 'wav', 'm4a', 'aac', 'flac', 'aiff', 'opus', 'oga', 'weba',
    // document
    'pdf',
  ],
  name: 'Media Viewer',
  component: LazyMediaEditor,
  readOnly: true,
  // Everything except SVG (which is text) is a binary format.
  binaryExtensions: [
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'ico',
    'mp4', 'webm', 'ogg', 'mov',
    'mp3', 'wav', 'm4a', 'aac', 'flac', 'aiff', 'opus', 'oga', 'weba',
    'pdf',
  ],
})

// ─── Parquet preview ─────────────────────────────────────────────────

const LazyParquetViewer = lazy(() =>
  import('../../ui/ParquetViewer.js').then(m => ({ default: m.ParquetViewer }))
)

registerPreview({
  extensions: ['parquet'],
  name: 'Parquet Preview',
  component: LazyParquetViewer,
})

// ─── Office file previews (read-only) — officecli-then-provider-gated (#617/#620) ─
// OfficeVersionViewer renders OOXML versions via officecli (flag-gated, no WOPI
// session) and falls back to the ACTIVE engine's read-only viewer — the DocsAPI
// viewer (OnlyOfficeDocViewer) under any DocsAPI provider (onlyoffice / eurooffice,
// §10), else Collabora (default) — for legacy/ODF, the flag-off case, or a render
// failure. VersionPreview checks the preview registry before the editor registry, so
// this registration decides the engine for office previews; the officecli render is a
// drop-in in front of the same provider-gated fallback.
const LazyOfficeViewer = lazy(() => import('../../ui/OfficeVersionViewer.js').then(m => ({ default: m.OfficeVersionViewer })))

// Both engines refresh in place on a version change (Collabora Reload_From_Storage;
// DocsAPI rotates document.key → refreshFile), so the registry keeps the component
// mounted and swaps the versionId prop rather than remounting.
const officeSupportsVersionRefresh = true

registerPreview({
  extensions: ['xlsx', 'xls', 'ods', 'fods', 'ots'],
  name: 'Spreadsheet Preview',
  component: LazyOfficeViewer,
  supportsVersionRefresh: officeSupportsVersionRefresh,
})

registerPreview({
  extensions: ['docx', 'doc', 'odt', 'fodt', 'ott', 'rtf'],
  name: 'Document Preview',
  component: LazyOfficeViewer,
  supportsVersionRefresh: officeSupportsVersionRefresh,
})

registerPreview({
  extensions: ['pptx', 'ppt', 'odp', 'fodp', 'otp'],
  name: 'Presentation Preview',
  component: LazyOfficeViewer,
  supportsVersionRefresh: true,
})
